import React, { useRef, useCallback } from 'react';
import { IRuleActionModel } from '../models/IRuleActionModel';
import { ValidationIcon } from 'domains/designers/common';
import { Callout } from 'components/layout/Callout';
import { ActionLinks } from 'components/ActionLinks/ActionLinks';
import { HasOpenState } from 'hooks/HasOpenState';
import { IActionLinkItem } from 'components/ActionLinks/ActionLinkModels';
import { BaseRuleActionHandler } from '../models/BaseRuleActionHandler';
import { ActionInlineEditor } from './ActionInlineEditor';
import { PhraseItem } from 'domains/designers/TruthTable/components';
import './ActionOutputItem.less';

interface IActionOutputItemProps {
  action: IRuleActionModel;
  handler: BaseRuleActionHandler;
  sessionId: string;
  isDebug: boolean;
}

/**
 * Represents rule action output, which will be triggered for truly outcome.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ActionOutputItem({ action, handler, sessionId, isDebug }: IActionOutputItemProps) {
  const { isOpen, toggleOpen, setClosed } = HasOpenState();
  const nameRef = useRef<HTMLButtonElement>(null);
  const actions: IActionLinkItem[] = handler.getMenuItems(nameRef.current).map(item => ({
    ...item,
    onClick: () => {
      item.onClick?.();
      setClosed();
    },
  }));

  const handleEdit = useCallback(
    (sourceRef: HTMLElement | null) => {
      handler.edit(sourceRef);
      setClosed();
    },
    [handler, setClosed]
  );

  const handleChange = useCallback(
    (sourceRef: HTMLElement | null) => {
      handler.change(sourceRef);
      setClosed();
    },
    [handler, setClosed]
  );

  const handleRemove = useCallback(
    (sourceRef: HTMLElement | null) => {
      handler.remove(sourceRef);
      setClosed();
    },
    [handler, setClosed]
  );

  return (
    <li className="action-output-item">
      <ValidationIcon className="action-output-item__validation" validationIssues={action.getValidationIssues()} />
      <PhraseItem ref={nameRef} className="action-output-item__name" onClick={toggleOpen} disabled={isDebug}>
        {action.ActionName}
      </PhraseItem>
      <ActionInlineEditor isDebug={isDebug} sessionId={sessionId} actionModel={action} handler={handler} />
      {action.canEdit() && !isDebug && (
        <button className="action-output-item__edit link" onClick={e => handleEdit(e.target as HTMLElement)}>
          Edit
        </button>
      )}
      {action.canChange() && !isDebug && (
        <button className="action-output-item__change link" onClick={e => handleChange(e.currentTarget)}>
          Change
        </button>
      )}
      {!isDebug && (
        <button className="action-output-item__delete link" onClick={e => handleRemove(e.currentTarget)}>
          Remove
        </button>
      )}
      <Callout
        isOpen={isOpen}
        onClose={setClosed}
        sourceRef={nameRef}
        placement="right"
        showArrow={true}
        flipBehavior="flip"
      >
        <ActionLinks values={{ current: actions }} />
      </Callout>
    </li>
  );
}
